import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Textarea, TextInput } from '@/components/ui/daisy';
import Lesson from '@/models/Lesson';

export type LessonFormFields = {
  name: string;
  description: string;
  objectives: string;
};

type LessonFormProps = {
  lesson?: Lesson;
  isLoading?: boolean;
  onSubmit: (data: LessonFormFields) => void;
};

export default function LessonForm({
  lesson,
  isLoading,
  onSubmit,
}: LessonFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonForm.form',
  });

  const [hasChanges, setHasChanges] = useState(false);

  const defaultValues = useMemo(
    () => ({
      name: lesson?.name ?? '',
      description: lesson?.description ?? '',
      objectives: lesson?.motivation?.message ?? '',
    }),
    [lesson],
  );

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LessonFormFields>({
    defaultValues,
  });

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      setHasChanges(false);
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <TextInput.Fieldset
        label={t('lessonName')}
        labelAlt={errors?.name?.message}
      >
        <TextInput
          type="text"
          placeholder={t('lessonNamePlaceholder')}
          className="w-full"
          color={!errors?.name?.message ? 'default' : 'error'}
          {...register('name', { required: t('lessonNameRequired') })}
        />
      </TextInput.Fieldset>
      <TextInput.Fieldset
        label={t('lessonDescription')}
        labelAlt={errors?.description?.message}
      >
        <TextInput
          type="text"
          placeholder={t('lessonDescriptionPlaceholder')}
          className="w-full"
          {...register('description')}
        />
      </TextInput.Fieldset>
      <Textarea.Fieldset label={t('lessonObjectives')}>
        <Textarea
          placeholder={t('lessonObjectivesPlaceholder')}
          className="w-full"
          {...register('objectives')}
        />
      </Textarea.Fieldset>
      <div className="flex justify-end">
        <Button
          type="submit"
          color="primary"
          className="px-16"
          disabled={isLoading || !hasChanges}
          loading={isLoading}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
